

import { Component, Vue, Watch, Prop } from "vue-property-decorator"
import { namespace, Getter, Action, State } from "vuex-class"

// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import vSelect from "vue-select"

import SkeletonLoader from "@/components/SkeletonLoader.vue"
import DataTable from "../DataTable.vue"
import FieldPermissions from "../Permissions/FieldPermissions.vue"
import CalculatedField from "./CalculatedField.vue"

import { TemplateDetails, Field, DataDefinition, CalculationType } from "@/types"
import User from "@/types/user"

import api from "@/services/api"
import { DataDef } from "vue/types/options"
import cloneDeep from "lodash/cloneDeep"

const setup = namespace("setup")

@Component({
	components: {
		"v-select": vSelect,
		"scrollbar": VuePerfectScrollbar,
		"data-table": DataTable,
		"field-permissions": FieldPermissions,
		"skeleton": SkeletonLoader
	}
})
export default class FieldSetup extends Vue {
	@Getter("getMe")
	public me!: User;

	@State tenantEnabledFeatures!: { [key: string]: boolean }

	@setup.Getter("getFieldTypes")
	fieldTypes!: any[];

	@setup.Getter("getDataSources")
	dataSources!: any[];

	@setup.Getter("getDefinitions")
	definitions!: DataDefinition[];

	@setup.Getter("getFieldTypesById")
	fieldTypesById!: { [key: string]: any };

	@setup.Action loadFields!: () => Promise<any[]>;

	@setup.State
	fields!: Field[];

	@Getter("getCaseName") getCaseName!: string;
	@Getter("getRecordName") getRecordName!: string;

	public editField: Field | null = null;
	public loading: boolean = true;

	public get inheritedDefinitions () {
		if (!this.editField || !this.editField.dataSourceId) {
			return this.definitions
		}

		const dataSource = this.dataSources.find((d: any) => d.id === this.editField!.dataSourceId)
		const dataDefinitions: DataDefinition[] = this.definitions.filter(d => d.id === dataSource.definitionId)

		if (!dataDefinitions || dataDefinitions.length === 0) {
			return this.definitions
		}

		const addDefinitions = (d: DataDefinition) => {
			if (d) {
				const children = this.definitions.filter(d2 => d2.inheritedFrom === d.id)

				children.forEach(childDefinition => {
					if (!dataDefinitions.find(added => added.id === childDefinition.id)) {
						dataDefinitions.push(childDefinition)
						addDefinitions(childDefinition)
					}
				})
			}
		}

		addDefinitions(dataDefinitions[0])

		return dataDefinitions
	}

	public get columns (): any[] {
		return [
			{
				key: "title",
				label: "Title",
				sortable: true,
				errors: (item: any) => item.title ? null : "Please enter a title"
			},
			{
				key: "description",
				label: "Description",
				dataType: "text-area",
				hasTemplate: true
			},
			{
				key: "dataTypeTitle",
				label: "Data Type",
				sortable: true,
				dataType: "list",
				valueColumn: "dataType",
				options: this.fieldTypes,
				errors: (item: any) => item.dataType ? null : "Please select a data type",
				readonly: !!(this.editField && this.editField.id),
				tooltip: "Please select from the available data types.  The data type will enforce the type of information you need to capture such as a time, data object, link, checkbox etc."
			},
			{
				key: "dataSourceTitle",
				label: "Data Source",
				dataType: "list",
				valueColumn: "dataSourceId",
				options: this.dataSources,
				limitOptions: true,
				sortable: true,
				inModal: (item: any) => item.dataType === "list" || item.dataType === "list-multiple" || item.dataType === "list-data",
				errors: (item: any) => item.dataType === "list" && !item.dataSourceId ? "Please select a data source" : null
			},
			{
				key: "definitionTitle",
				label: "Data Definition",
				dataType: "list",
				valueColumn: "definitionId",
				options: this.inheritedDefinitions,
				sortable: true,
				inModal: (item: any) => item.dataType === "list-data",
				errors: (item: any) => item.dataType === "list-data" && !item.definitionId && !item.dataSourceId ? "Please select a data source" : null
			},
			{
				key: "extensions",
				label: null,
				inModal: (item: any) => true,
				dynamicComponent: CalculatedField,
				inTable: false,
				errors: (item: any) => {
					if (!item || !item.extensions || item.dataType !== "calculated") {
						return null
					}

					if (item.extensions.calculationType === null) {
						return "Select a calculation type"
					}

					switch (item.extensions.calculationType) {
						case CalculationType.DaysUntilOrFromToday:
						case CalculationType.AddToDate:
							return item.extensions.dateField ? null : "Please enter a date field"
						case CalculationType.Total:
						case CalculationType.Average:
							return item.extensions.dataListField
								? item.extensions.dataItemField ? null : "Please enter a data item field"
								: "Please enter a data field"
						case CalculationType.DaysBetweenDates:
							return item.extensions.startField
								? item.extensions.endField ? null : "Please enter an end field"
								: "Please enter a start field"
						case CalculationType.GreaterThan:
						case CalculationType.LessThan:
							return item.extensions.checkField ? null : "Please enter a field"
						case CalculationType.TimeSpentAs:
							return item.extensions.trackingFieldId ? null : "Please enter a field to track"
						default:
							return null
					}
				},
				componentProperties: {
					tenantEnabledFeatures: this.tenantEnabledFeatures
				}
			},
			{
				key: "allowFiles",
				label: "Allow Files",
				sortable: true,
				hasTemplate: true,
				dataType: "checkbox",
				inModal: (item: any) => item.dataType !== "user-role",
				tooltip: "Allow you to upload a file with the field."
			},
			{
				key: "displayReadOnly",
				label: "Display as Read Only",
				inTable: false,
				dataType: "checkbox",
				inModal: (item: any) => item.dataType !== "case-number" && item.dataType !== "calculated",
				tooltip: "Field displays as read-only"
			},
			{
				key: "permissions.record",
				label: this.getRecordName + " Permissions",
				hasTemplate: true,
				dataType: "permissions"
			},
			{
				key: "permissions.case",
				label: this.getCaseName + " Permissions",
				hasTemplate: true,
				dataType: "permissions"
			}
		]
	}

	loadingFields: boolean = false;

	public async mounted () {
		await this.loadFields()
		this.loading = false
	}

	async saveField (field: Field) {
		if (field.dataType === "calculated") {
			CalculatedField.stripUnusedOptions(field)
		}

		await api.fieldSave(field)
		this.loadFields()
	}

	async deleteField (field: Field) {
		await api.fieldDelete(field)
		this.loadFields()
	}

	createNewItem () {
		return this.editField = {
			title: "",
			description: null,
			dataType: null,
			dataSourceId: null,
			dataSourceTitle: null,
			definitionId: null,
			definitionTitle: null,
			allowFiles: false,

			permissions: {
				record: { view: [], edit: [] },
				case: { view: [], edit: [] }
			}
		} as Field
	}

	setEditItem (editItem: Field) {
		this.editField = editItem
		return cloneDeep(editItem)
	}

	async deleteDetails (field: Field) {
		if (!field!.id) return
		return api.fieldUsage(field)
	}
}
